// @givmo/app-charity — the intake doors, addressed by URL (CBO-IN4).
//
// The nine routes hanging off ONE provisioned enrollment record: how its history comes in, the
// account map behind a history carry, the opening figures a cut-in states, the run that posts them,
// and the records a run leaves behind. Same hatch as `enrollment-transport.ts` for the same reason —
// these routes post-date the committed OpenAPI snapshot, so there is no generated operation, no
// generated type, and no query-option factory for any of them. That file's header carries the whole
// rationale and the graduation note; nothing about it differs here.
//
// ══ WHAT THIS MODULE DELIBERATELY DOES NOT OFFER ══════════════════════════════════════════════
//
// **No bulk anything.** There is no confirm-all, no resolve-remaining, no multi-row body. The
// backend has no list-shaped resolution route at all, and that is a ruling rather than an omission:
// a "looks right, import it" step is the whole-document confirmation the suite's document rules say
// exists nowhere in onboarding, and it would be that step whatever it was named. Every function
// below that records an answer names exactly ONE row.
//
// **No map delete.** A map version is superseded, never removed — an import record pins the version
// it ran against by content hash, so removing one would leave a record naming content that no longer
// exists. `PUT …/maps` is the only write, and it appends.
//
// **No run listing.** The backend exposes no `GET …/import-runs`, and this module invents none. A
// run reaches this client twice and only twice: as the answer to starting one, and — because the
// idempotency key is the caller's own — as the answer to REPLAYING that same key, which returns the
// run it already named rather than running again. What survives a page reload is the onboarding
// record a bound run wrote, and the record's event log.
//
// ══ THE ONE CONTRACT FACT THAT SHAPES EVERY CALLER ════════════════════════════════════════════
//
// **A refusal arrives in TWO shapes, and they mean the same thing.** Starting a run that the backend
// refuses raises — the client sees a 409 (the state disagrees) or a 503 (a rail whose input this
// deployment does not hold). But the refusal is ALSO written onto the run row, so replaying the same
// idempotency key answers `200` with `run_state: "refused"` and the reason on it. A screen that
// rendered the first as an error and the second as a result would be describing one event two ways.
// `enrollment-backfill-decode.ts` reads both into the same shape.
//
// ⚠ Which refusals SPEND the key is not uniform, and it is deliberate: the gates a person can fix in
// place (nothing chosen yet, a row still unanswered, a figure still unrecorded) refuse BEFORE the run
// row is written, so pressing the control early is fully recoverable — fix what it named, press it
// again. Only a refusal that happened after the attempt was recorded consumes the key.

import { givmoClient } from '@givmo/api-client';
import { charityBase } from './enrollment-transport';

/** `…/enrollments/{id}` — the one record every door below hangs off. */
function recordBase(charityId: string, enrollmentId: string): string {
  return `${charityBase(charityId)}/${encodeURIComponent(enrollmentId)}`;
}

/**
 * Pagination is `page[limit]` / `page[offset]`, the repo-wide convention. A bare `limit` is silently
 * ignored by the backend, so the page size would become whatever its default is with nothing to say
 * so.
 */
const LIMIT_PARAM = 'page[limit]';
const OFFSET_PARAM = 'page[offset]';

/**
 * The page sizes these listings request — each the backend's own declared maximum for that route.
 *
 * ⚠ ASKING FOR THE MAXIMUM IS NOT A COMPLETENESS CLAIM, and no caller may treat it as one. It is a
 * legibility choice: the mapping queue and the opening figures are worked through until nothing is
 * left, so splitting them across pages would make a person answer rows on page one and then discover
 * page two. What decides whether anything remains is the SERVER, which sees the whole set and refuses
 * a run naming what is still waiting; the screens render that refusal rather than computing their own.
 */
export const MAPPING_ROWS_PAGE_SIZE = 200;
export const OPENING_BALANCES_PAGE_SIZE = 500;
export const ONBOARDING_RECORDS_PAGE_SIZE = 200;

/** Append a query string, omitting every absent value so no empty parameter is ever transmitted. */
function withQuery(
  url: string,
  params: Record<string, string | number | boolean | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query.length === 0 ? url : `${url}?${query}`;
}

/** A page request against one of the listings. */
export interface BackfillPage {
  limit?: number;
  offset?: number;
}

// ── The recorded choice ────────────────────────────────────────────────────────────────────────

/**
 * How this organization's history comes in. `opening_balances` or `full_history`.
 *
 * ⚠ ONCE. Recording the SAME choice again is a replay and answers with the record's current state;
 * recording a DIFFERENT one answers 409, because the two are different shapes rather than two
 * settings of one dial and doors have already rendered against the first. Answers the enrollment
 * record with its event log — the same shape `getEnrollment` returns.
 */
export async function recordBackfillChoice(
  charityId: string,
  enrollmentId: string,
  backfillChoice: string,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${recordBase(charityId, enrollmentId)}/backfill-choice`,
    body: { backfill_choice: backfillChoice },
    throwOnError: true,
  });
  return data;
}

// ── The maps ───────────────────────────────────────────────────────────────────────────────────

/** The map-recording body. `document` is the engine's own declaration shape, passed through whole. */
export interface EntityMapBody {
  map_kind: string;
  document: Record<string, unknown>;
  unmapped_subjects?: string[];
}

/**
 * Record one rail's account map and put every subject it names in front of a reviewer.
 *
 * ⚠ NO SCREEN IN THIS CONSOLE CALLS THIS TODAY, and that is a product decision rather than an
 * oversight. The document is an operator-scale artifact — the engine's own strict declaration, refused
 * whole on an unknown key — and a JSON editor is not a surface to put in front of an organization.
 * What the console offers is the REVIEW of the rows a map seeds, which is where the control actually
 * lives. It is written and tested here because a transport is the module's complete address book: the
 * day a map-authoring surface exists, the question is what it renders, not whether the call is right.
 *
 * A refused document answers 400 carrying the engine loader's own message, which names the offending
 * key or the books the map claims. Render it verbatim — this client cannot say it better.
 */
export async function putEntityMaps(
  charityId: string,
  enrollmentId: string,
  body: EntityMapBody,
): Promise<unknown> {
  const { data } = await givmoClient.put({
    url: `${recordBase(charityId, enrollmentId)}/maps`,
    body,
    throwOnError: true,
  });
  return data;
}

/** A mapping-queue request. `unresolvedOnly` is the working view; `mapKind` narrows to one rail. */
export interface MappingRowsQuery extends BackfillPage {
  mapKind?: string;
  unresolvedOnly?: boolean;
}

/**
 * Every source account and merchant, and where each one posts.
 *
 * ⚠ `unresolvedOnly` INCLUDES DECLINED ROWS, and the copy must not call the view "not yet looked at".
 * A row somebody looked at and would not answer blocks a run exactly as an untouched one does, and
 * hiding it would leave a run refused by something nobody could find.
 */
export async function listMappingRows(
  charityId: string,
  enrollmentId: string,
  query: MappingRowsQuery = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: withQuery(`${recordBase(charityId, enrollmentId)}/mapping-rows`, {
      [LIMIT_PARAM]: query.limit ?? MAPPING_ROWS_PAGE_SIZE,
      [OFFSET_PARAM]: query.offset,
      map_kind: query.mapKind,
      // Omitted rather than transmitted as `false`: absent IS the backend's own default, and a
      // parameter this client never varies is one more thing for a reader to wonder about.
      unresolved_only: query.unresolvedOnly === true ? true : undefined,
    }),
    throwOnError: true,
  });
  return data;
}

/**
 * The resolution body for one mapping row.
 *
 * ⚠ THE THREE ANSWERS TAKE DIFFERENT BODIES, and sending the wrong combination is refused rather
 * than ignored: a confirmation carries no `account_code` (a different code is a CORRECTION, and the
 * backend says so by name), a correction requires one, and a decline refuses to carry one at all.
 * `pending` is not an answer, and a body carrying it is refused.
 */
export interface MappingRowResolutionBody {
  resolution: string;
  account_code?: string;
  note?: string;
}

/** Confirm, correct or decline ONE source account or merchant. There is no bulk form. */
export async function resolveMappingRow(
  charityId: string,
  enrollmentId: string,
  mappingRowId: string,
  body: MappingRowResolutionBody,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${recordBase(charityId, enrollmentId)}/mapping-rows/${encodeURIComponent(mappingRowId)}/resolution`,
    body,
    throwOnError: true,
  });
  return data;
}

// ── The opening balances ───────────────────────────────────────────────────────────────────────

/** One account put in front of the organization, with the figure proposed for it. */
export interface OpeningBalanceSeed {
  account_code: string;
  proposed_amount_cents?: number | null;
}

/**
 * Put accounts in front of the organization, each with the figure proposed for it.
 *
 * ⚠ THIS PROPOSES; IT RECORDS NOTHING AGAINST THE ORGANIZATION. Every account is then answered on
 * its own through `resolveOpeningBalanceField`, and the row keeps which of the three answers a person
 * gave. Seeding is therefore not a back door around the per-field rule — a seeded account is
 * `pending` until somebody answers it, and a run refuses while any account is unanswered.
 *
 * Re-seeding an account whose proposed figure has CHANGED returns it to unanswered: a figure that
 * moved has not been agreed to. Re-seeding an unchanged one is a no-op.
 *
 * Answers `{enrollment_id, fields_seeded, fields_reopened}` — counts, not rows.
 */
export async function seedOpeningBalanceFields(
  charityId: string,
  enrollmentId: string,
  fields: readonly OpeningBalanceSeed[],
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${recordBase(charityId, enrollmentId)}/opening-balances`,
    body: fields,
    throwOnError: true,
  });
  return data;
}

/** An opening-figures request. */
export interface OpeningBalancesQuery extends BackfillPage {
  unresolvedOnly?: boolean;
}

/**
 * Each account's opening figure — what was proposed, and what the organization recorded.
 *
 * Amounts are debit-positive CENTS; a credit balance is negative, the convention a trial balance
 * uses.
 */
export async function listOpeningBalanceFields(
  charityId: string,
  enrollmentId: string,
  query: OpeningBalancesQuery = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: withQuery(`${recordBase(charityId, enrollmentId)}/opening-balances`, {
      [LIMIT_PARAM]: query.limit ?? OPENING_BALANCES_PAGE_SIZE,
      [OFFSET_PARAM]: query.offset,
      unresolved_only: query.unresolvedOnly === true ? true : undefined,
    }),
    throwOnError: true,
  });
  return data;
}

/**
 * The resolution body for one opening figure.
 *
 * ⚠ SAME THREE-SHAPED CONTRACT AS A MAPPING ROW, and one addition that matters: `null` and `0` are
 * DIFFERENT answers. A confirmed zero is a real statement — this account carries no opening balance —
 * and it reaches the builder as a zero line. An absent figure is not a zero and never becomes one.
 */
export interface OpeningBalanceResolutionBody {
  resolution: string;
  amount_cents?: number;
  source_note?: string;
}

/** Confirm, correct or decline ONE account's opening figure. There is no bulk form. */
export async function resolveOpeningBalanceField(
  charityId: string,
  enrollmentId: string,
  fieldId: string,
  body: OpeningBalanceResolutionBody,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${recordBase(charityId, enrollmentId)}/opening-balances/${encodeURIComponent(fieldId)}/resolution`,
    body,
    throwOnError: true,
  });
  return data;
}

// ── The run and its records ────────────────────────────────────────────────────────────────────

/**
 * The body that starts one import run.
 *
 * `idempotency_key` is REQUIRED and is the caller's own: sending the same key again answers with the
 * run it already named rather than running a second time. A second import over the same window is a
 * real second act, and only the caller knows which one this is — so the key is a decision this
 * console makes deliberately, never a value it regenerates on every click.
 *
 * `tie_waived` is an operator act stamped onto the run, and it is REFUSED on an opening-balance
 * cut-in: there is no source population to tie against, and what stands in its place — the mapping
 * summing to zero — is not waivable. This console never sends it.
 */
export interface ImportRunBody {
  rail: string;
  idempotency_key: string;
  period?: string;
  effective_date?: string;
  memo?: string;
  tie_waived?: boolean;
  tie_waiver_reason?: string;
}

/**
 * Bring history into the organization's books.
 *
 * ⚠ THE RESPONSE IS THE RUN'S TRUTHFUL STATE, not a promise. It may have bound its record, or it may
 * name why it did not. Runs are refused, not queued — nothing retries in the background — so a screen
 * that rendered "we'll keep trying" would be describing machinery that does not exist.
 */
export async function startImportRun(
  charityId: string,
  enrollmentId: string,
  body: ImportRunBody,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${recordBase(charityId, enrollmentId)}/import-runs`,
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * The organization's onboarding records, oldest first.
 *
 * ⚠ A STACK, AND ONLY THAT — one entry for the books being opened, then one for each import that
 * brought something in. It is deliberately NOT a completion artifact: no organization arrives with
 * complete historical books, so nothing in it says setup is finished, and no screen may present it as
 * though it did.
 */
export async function listOnboardingRecords(
  charityId: string,
  enrollmentId: string,
  page?: BackfillPage,
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: withQuery(`${recordBase(charityId, enrollmentId)}/onboarding-records`, {
      [LIMIT_PARAM]: page?.limit ?? ONBOARDING_RECORDS_PAGE_SIZE,
      [OFFSET_PARAM]: page?.offset,
    }),
    throwOnError: true,
  });
  return data;
}
